"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Gantt = Gantt;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const shared_1 = require("@forecastify/shared");
const cn_1 = require("../lib/cn");
const color_1 = require("../lib/color");
const misc_1 = require("./misc");
const tooltip_1 = require("./tooltip");
const truncated_text_1 = require("./truncated-text");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The scheduling timeline. Bars, dependency arrows, three zooms.             │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **Built to the design system's own contract** — `components/data/Gantt.d.ts` declares the props
 * and `Gantt.prompt.md` the behaviour: *"Bars are 5px-radius, critical path is marked, dependency
 * conflicts are reported in words (\"Két függőség sérül\") rather than blocked silently."* The
 * contract exists and no implementation ships with it, so this is that implementation rather than an
 * invention. Prop names, defaults and the shape of `rows` are the contract's, unchanged.
 *
 * ⚠️ **Conflicts are REPORTED, not refused.** A dependency violation — a successor starting before
 * its predecessor finishes — is a fact about the plan, and plans are wrong before they are right.
 * Blocking the drag would mean a plan can never be edited into a valid state through an invalid one.
 * So a violated arrow is drawn in the destructive colour and `footer` says how many, in words.
 *
 * ⚠️ **Dates in, dates out. This component does no timezone maths.** Everything is a wire date
 * (`yyyy-mm-dd`) and the arithmetic goes through `@forecastify/shared`'s helpers — `hard-won.md` has
 * the driver rows on why a calendar date and an instant cannot share a converter.
 */
/**
 * How far the pointer may travel before the gesture stops being a click.
 *
 * ⚠️ Four pixels is below the distance a hand moves while pressing a button and well under the
 * narrowest day column (5.5px at the quarter zoom), so it separates the two gestures at every zoom.
 */
const DRAG_SLOP = 4;
function Gantt({ rows, rangeFrom, rangeTo, labels, unitWidth = 26, rowHeight = 30, nameWidth = 250, assigneeWidth = 150, columnStrip, columnTint, ruleEveryDay = false, unit = 'day', today, centerToken, readOnly = false, onMove, onOpen, className, }) {
    const scroller = (0, react_1.useRef)(null);
    /** Whether the chart is drawn at all — it is not while there are no rows. */
    const drawn = rows.length > 0;
    const [drag, setDrag] = (0, react_1.useState)(null);
    /**
     * Whether the pointer moved far enough to call the gesture a drag.
     *
     * ⚠️ **A ref, not state.** It is read by the `click` that fires immediately after `pointerup`,
     * and a state update would not have landed by then — the open would win the race it is meant to
     * lose. Nothing renders from it either, so there is nothing to re-render for.
     */
    const dragged = (0, react_1.useRef)(false);
    /**
     * Open the row, unless the gesture that ended was a drag.
     *
     * ⚠️ **The flag is CONSUMED here rather than cleared on the next pointerdown.** A drag that ends
     * outside any bar fires no click at all, so a flag waiting to be cleared by the next press would
     * swallow the press after it — one lost click per drag that left the chart.
     */
    const openIfClicked = (key) => {
        const wasDrag = dragged.current;
        dragged.current = false;
        if (!wasDrag)
            onOpen?.(key);
    };
    /* ⚠️ An unparseable range is one day wide rather than NaN wide: `width: NaN` renders as zero
     * and the chart silently disappears, which is the least diagnosable outcome available. */
    /**
     * ⚠️⚠️ **INCLUSIVE OF `rangeTo`, AND THE `+ 1` IS NOT COSMETIC.** `dayCount` answers the days
     * BETWEEN two dates, so a range of 1–31 August is 30 — and the chart drew 30 columns, leaving the
     * 31st with nowhere to sit. A bar ending on the last day of the range then drew at the right edge
     * with zero width, which reads as a missing task rather than as a boundary being off by one.
     * Found by counting the columns a month view produced.
     */
    const totalDays = (0, react_1.useMemo)(() => Math.max(1, daysOr(rangeFrom, rangeTo, 1) + 1), [rangeFrom, rangeTo]);
    /* ⚠️ One scale, and everything that draws asks it where a day sits — see `buildScale`. */
    const scale = (0, react_1.useMemo)(() => buildScale(rangeFrom, totalDays, unit, unitWidth, labels.months, today), [rangeFrom, totalDays, unit, unitWidth, labels.months, today]);
    /**
     * ⚠️ **Re-centre on `centerToken`, not on `today`.** The contract's own mechanism, and the reason
     * is that a caller wants to say *"put today back in the middle"* repeatedly — pressing the Ma
     * button twice must scroll twice. Keying the effect on the date would make the second press do
     * nothing, because the date has not changed.
     */
    (0, react_1.useEffect)(() => {
        const element = scroller.current;
        if (element === null || today === undefined)
            return;
        const offset = scale.x[Math.max(0, daysOr(rangeFrom, today, 0))] ?? 0;
        element.scrollLeft = Math.max(0, offset - element.clientWidth / 2);
        /* ⚠️⚠️ `drawn` IS A DEPENDENCY BECAUSE THE SCROLLER DOES NOT EXIST UNTIL THERE ARE ROWS. The
           chart returns its empty state while the tasks load, so the first run found no scroller and
           returned — and nothing it depended on changed when the rows arrived, so the timeline opened
           at the far left of its range, a year before today, with every bar off to the right. */
    }, [centerToken, today, rangeFrom, scale, drawn]);
    /**
     * ⚠️⚠️ **THE POINTER LISTENERS GO ON `window`, NOT ON THE BAR.** A drag that leaves the element —
     * which every drag does, that being the point — stops receiving events from it, so a bar-local
     * `onPointerMove` tracks for about ten pixels and then freezes. `setPointerCapture` is the other
     * answer and behaves differently across browsers on a scrolling container.
     *
     * ⚠️ And they are removed on cleanup: a listener that survives the drag keeps moving the bar
     * after the mouse is up, which reads as the UI having a mind of its own.
     */
    (0, react_1.useEffect)(() => {
        if (drag === null)
            return;
        const move = (event) => {
            /**
             * ⚠️⚠️ **THE PIXEL THRESHOLD IS WHAT STOPS A DRAG FROM ALSO OPENING THE TASK.** A pointer
             * that goes down and up on a bar fires `click`, and the bar's click opens the drawer — so
             * dragging a row opened the task every time, over the plan the person was reading. Reported
             * exactly that way.
             *
             * ⚠️ **PIXELS, NOT DAYS.** At the month zoom a day is 5.5px, but at the day zoom it is 26 —
             * so a 20px drag rounds to zero days and the old `days === 0` test would have called it a
             * click. What makes it a drag is that the pointer moved, not that the result changed.
             */
            if (Math.abs(event.clientX - drag.fromX) > DRAG_SLOP)
                dragged.current = true;
            /* ⚠️ `scale.dayWidth` is an AVERAGE at the month and quarter units — see its note. A drag
               is a gesture, not a measurement, and the alternative is a bar that moves by different
               amounts depending on which month it started in. */
            const days = Math.round((event.clientX - drag.fromX) / scale.dayWidth);
            if (days !== drag.days)
                setDrag({ ...drag, days });
        };
        const up = () => {
            setDrag(null);
            if (drag.days === 0 || onMove === undefined)
                return;
            const next = shifted(drag);
            /* ⚠️ A resize that would invert the bar is dropped rather than clamped: clamping silently
             * turns "I dragged too far" into a one-day task, and the person cannot tell it happened. */
            if (next.end < next.start)
                return;
            onMove(drag.key, next.start, next.end);
        };
        window.addEventListener('pointermove', move);
        window.addEventListener('pointerup', up);
        return () => {
            window.removeEventListener('pointermove', move);
            window.removeEventListener('pointerup', up);
        };
    }, [drag, scale, onMove]);
    const conflicts = rows.reduce((total, row) => total + (row.dependencies ?? []).filter((d) => d.violated === true).length, 0);
    if (rows.length === 0) {
        return ((0, jsx_runtime_1.jsx)("div", { className: (0, cn_1.cn)('text-muted-foreground p-8 text-center text-[12.5px]', className), children: labels.empty }));
    }
    /**
     * ┌───────────────────────────────────────────────────────────────────────────┐
     * │ ⚠️⚠️ THE HEADER IS TWO BANDS DEEP, AND EVERY VERTICAL OFFSET READS THIS.  │
     * └───────────────────────────────────────────────────────────────────────────┘
     *
     * The upper band names the period — the month over a ruler of days, the year over a ruler of
     * months — and the lower one is the unit itself. Item 31's bug was a single-band header and bars
     * that forgot to skip it; the defence against its return is that `headerHeight` is computed once
     * here and is the only thing any offset is allowed to add.
     */
    const headerHeight = rowHeight * 2;
    return ((0, jsx_runtime_1.jsxs)("div", { className: (0, cn_1.cn)('flex min-h-0 flex-1 flex-col', className), children: [(0, jsx_runtime_1.jsx)("div", { ref: scroller, role: "group", "aria-label": labels.timeline, className: "relative min-h-0 flex-1 overflow-auto", "data-gantt-conflicts": conflicts, children: (0, jsx_runtime_1.jsxs)("div", { className: "flex min-w-max", children: [(0, jsx_runtime_1.jsxs)("div", { 
                            /* ⚠️ `z-30` — above the chart's own header at 20, which is what stops the scrolling
                               grid showing through the frozen columns. `bg-background` is load-bearing with it: a
                               transparent sticky column hides nothing, whatever its z-index. */
                            className: "bg-background sticky left-0 z-30 shrink-0 border-r", style: { width: nameWidth + assigneeWidth }, children: [(0, jsx_runtime_1.jsxs)("div", { 
                                    /* ⚠️ `z-40` — the corner is sticky on BOTH axes and has to beat both neighbours. */
                                    className: "bg-[color-mix(in_oklch,var(--muted)_40%,var(--background))] text-muted-foreground sticky top-0 z-40 flex items-center border-b text-[11px] font-medium tracking-wide uppercase", style: { height: headerHeight }, children: [(0, jsx_runtime_1.jsx)("span", { className: "truncate px-3", style: { width: nameWidth }, children: labels.name }), assigneeWidth === 0 ? null : ((0, jsx_runtime_1.jsx)("span", { className: "truncate border-l px-3", style: { width: assigneeWidth }, children: labels.assignee }))] }), rows.map((row) => {
                                    /* ⚠️ The caller's own cell where it gave one — see `GanttRow.lead`. */
                                    const inside = row.lead ?? ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsx)("span", { className: "size-2 shrink-0 rounded-[3px]", style: { background: row.color }, "aria-hidden": true }), (0, jsx_runtime_1.jsx)("span", { className: "text-muted-foreground shrink-0 text-[11px]", children: row.key }), (0, jsx_runtime_1.jsx)(truncated_text_1.TruncatedText, { className: "flex-1 text-[12px]", children: row.title })] }));
                                    /**
                                     * ⚠️⚠️ **THE NAME OPENS THE ROW — item 31.** The left-hand title opens the drawer.
                                     * Only the BAR was clickable, so a task with no dates — or one scrolled off the
                                     * right of a long range — had a name on screen and no way to reach it. The name is
                                     * always visible; the bar is not.
                                     *
                                     * ⚠️ **A real button element**, for the reason the task title's own rename records:
                                     * a click handler on a div is unreachable by keyboard and announces nothing.
                                     *
                                     * ⚠️ **`openIfClicked`, not `onOpen`** — the same guard the bar uses. A drag that
                                     * ends over the name must not also open it.
                                     */
                                    const name = onOpen === undefined ? ((0, jsx_runtime_1.jsx)("div", { className: "flex h-full items-center gap-2 px-3", style: { width: nameWidth }, children: inside })) : (
                                    /* ⚠️ The kit's tooltip, never the browser's `title`: the default name explains
                                       itself only when cut off (`TruncatedText` above); a caller's own `lead` cannot
                                       be measured from here, so its row keeps the title on every hover. */
                                    (0, jsx_runtime_1.jsx)(tooltip_1.Tooltip, { asChild: true, content: row.lead === undefined ? undefined : row.title, children: (0, jsx_runtime_1.jsx)("button", { type: "button", className: "hover:bg-accent/40 flex h-full items-center gap-2 px-3 text-left transition-colors", style: { width: nameWidth }, onClick: () => {
                                                openIfClicked(row.key);
                                            }, children: inside }) }));
                                    return ((0, jsx_runtime_1.jsxs)("div", { className: "flex items-stretch border-b", style: { height: rowHeight }, children: [name, assigneeWidth === 0 ? null : ((0, jsx_runtime_1.jsx)("div", { className: "flex min-w-0 items-center gap-1.5 border-l px-2", style: { width: assigneeWidth }, children: row.assignee === undefined ? (
                                                /* ⚠️ The dash carries the word as its title and as its accessible name: an
                                                 em-dash announces as nothing at all under a screen reader, so the row
                                                 would simply have no assignee column rather than an empty one. */
                                                (0, jsx_runtime_1.jsx)(tooltip_1.Tooltip, { asChild: true, content: labels.unassigned, children: (0, jsx_runtime_1.jsx)("span", { className: "text-muted-foreground/50 text-[12px]", "aria-label": labels.unassigned, children: "\u2013" }) })) : ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsx)(misc_1.Avatar, { src: row.assignee.avatarSrc, seed: row.assignee.avatarSeed, fallback: row.assignee.initials ?? '', color: row.assignee.color, title: row.assignee.name, size: 18 }), (0, jsx_runtime_1.jsx)("span", { className: "min-w-0 flex-1 truncate text-[12px]", children: row.assignee.name })] })) }))] }, row.key));
                                })] }), (0, jsx_runtime_1.jsxs)("div", { className: "relative shrink-0", style: {
                                width: scale.width,
                                height: rows.length * rowHeight + headerHeight,
                            }, children: [(0, jsx_runtime_1.jsx)(ScaleHeader, { scale: scale, headerHeight: headerHeight, rangeFrom: rangeFrom, ...(columnStrip === undefined ? {} : { columnStrip }), ...(columnTint === undefined ? {} : { columnTint }), ruleEveryDay: ruleEveryDay }), today === undefined ? null : ((0, jsx_runtime_1.jsx)(tooltip_1.Tooltip, { asChild: true, content: labels.today, children: (0, jsx_runtime_1.jsx)("div", { className: "bg-primary/60 absolute top-0 bottom-0 w-px", style: { left: scale.x[Math.max(0, daysOr(rangeFrom, today, 0))] ?? 0 }, "aria-hidden": true }) })), rows.flatMap((row, index) => (row.bars ?? [row]).map((band) => {
                                    const one = band === row
                                        ? row
                                        : /* ⚠️ The band's dates, colour and label over the row's; no dependencies,
                                             because an arrow between bands is not a thing this chart draws. */
                                            { ...row, ...band, bars: undefined, dependencies: undefined };
                                    return ((0, jsx_runtime_1.jsx)(Bar, { row: one, rows: rows, index: index, rangeFrom: rangeFrom, scale: scale, rowHeight: rowHeight, headerHeight: headerHeight, drag: drag?.key === one.key ? drag : null, 
                                        /* ⚠️ Forced for a banded row — `GanttRow.bars` says why a drag has nothing to
                                           write back to. */
                                        readOnly: readOnly || row.bars !== undefined, labels: labels, onOpen: openIfClicked, onDragStart: (edge, clientX) => {
                                            setDrag({
                                                key: one.key,
                                                edge,
                                                fromX: clientX,
                                                start: one.start,
                                                end: one.end,
                                                days: 0,
                                            });
                                        } }, one.key));
                                }))] })] }) }), conflicts > 0 ? ((0, jsx_runtime_1.jsx)("p", { className: "text-destructive border-t px-3 py-2 text-[12px]", role: "status", children: labels.conflicts(conflicts) })) : null] }));
}
function buildScale(rangeFrom, totalDays, unit, unitWidth, months, today) {
    const monthName = (at) => months?.[at.getMonth()] ?? `${String(at.getMonth() + 1)}.`;
    const columns = [];
    const bands = [];
    /* ⚠️ The walk is day by day, for the reason `ruler` carried before it: month arithmetic — 31
       January plus one month — is the single richest source of off-by-one date bugs there is, and
       starting a new column when the month NUMBER changes cannot get it wrong. */
    const close = (into, at) => {
        const last = into.at(-1);
        if (last !== undefined)
            into[into.length - 1] = { ...last, days: at - last.at };
    };
    for (let day = 0; day < totalDays; day += 1) {
        const date = shift(rangeFrom, day);
        const parsed = date === null ? null : parse(date);
        if (parsed === null)
            continue;
        const first = day === 0;
        const monthStart = parsed.getDate() === 1;
        const quarterStart = monthStart && parsed.getMonth() % 3 === 0;
        const yearStart = monthStart && parsed.getMonth() === 0;
        /* `getDay()` is 0 for Sunday; 1 is Monday, which is where a Hungarian week starts. */
        const weekStart = parsed.getDay() === 1;
        const openColumn = (label, strong, divider = true) => {
            close(columns, day);
            columns.push({ at: day, days: totalDays - day, label, strong, divider });
        };
        const openBand = (label, strong) => {
            close(bands, day);
            bands.push({ at: day, days: totalDays - day, label, strong, divider: true });
        };
        if (unit === 'day') {
            /* ⚠️ The divider is the WEEK, not the day — see `Column.divider`. */
            openColumn(String(parsed.getDate()), weekStart, weekStart || first);
            if (first || monthStart) {
                openBand(`${monthName(parsed)} ${String(parsed.getFullYear())}`, true);
            }
            continue;
        }
        if (unit === 'month') {
            if (first || monthStart)
                openColumn(monthName(parsed), quarterStart);
            if (first || yearStart)
                openBand(String(parsed.getFullYear()), true);
            continue;
        }
        /* quarter — ⚠️ named by its months rather than "Q3", which is what Jira writes and what
           somebody reading a delivery plan actually thinks in. */
        if (first || quarterStart) {
            const firstMonth = Math.floor(parsed.getMonth() / 3) * 3;
            const lastMonth = firstMonth + 2;
            const name = `${months?.[firstMonth] ?? String(firstMonth + 1)} – ${months?.[lastMonth] ?? String(lastMonth + 1)}`;
            openColumn(name, quarterStart);
        }
        if (first || yearStart)
            openBand(String(parsed.getFullYear()), true);
    }
    /* ⚠️ The last column of each row runs to the end of the range rather than to the next boundary,
       which `close` never reaches because there is no next one. */
    const width = columns.length * unitWidth;
    /**
     * ⚠️ **The lookup includes `totalDays` itself**, because a bar's RIGHT edge is the left edge of
     * the day after its last — an inclusive end drawn as an exclusive boundary. Without the extra
     * entry the final day of the range draws zero-width.
     */
    const x = new Float64Array(totalDays + 1);
    let column = 0;
    for (let day = 0; day <= totalDays; day += 1) {
        while (column + 1 < columns.length && day >= (columns[column + 1]?.at ?? totalDays))
            column += 1;
        const current = columns[column];
        if (current === undefined) {
            x[day] = day * unitWidth;
            continue;
        }
        const within = current.days === 0 ? 0 : (day - current.at) / current.days;
        x[day] = (column + within) * unitWidth;
    }
    /* ⚠️ Labels are dropped rather than overlapped where a column is narrower than its own text.
       Overlapping digits are what read as a rendering fault in the ruler this replaced. */
    const sized = (cells, need) => cells.map((cell) => {
        const from = x[cell.at] ?? 0;
        const to = x[Math.min(totalDays, cell.at + cell.days)] ?? width;
        return to - from < need ? { ...cell, label: '' } : cell;
    });
    return {
        x,
        columns: sized(columns, 20),
        bands: sized(bands, 46),
        width,
        highlight: periodOf(rangeFrom, totalDays, unit, today),
        dayWidth: totalDays === 0 ? unitWidth : width / totalDays,
    };
}
/**
 * The span of the period today falls in, in day indices.
 *
 * ⚠️ **Computed from the DATE rather than looked up in `columns`**, because the period is not always
 * a column: at the day unit the band is a WEEK and the columns are days. Deriving it from the
 * calendar is also the only version that is right when the range clips the period — a week that
 * starts before `rangeFrom` is clamped to the range and still highlights the part you can see.
 *
 * ⚠️ **`null` when today is outside the range**, which is an ordinary state: somebody looking at
 * next year's plan should see no band rather than one pinned to an edge.
 */
function periodOf(rangeFrom, totalDays, unit, today) {
    if (today === undefined)
        return null;
    const offset = dayCount(rangeFrom, today);
    /* ⚠️ `parse` answers null for a well-formed string naming no real day — `2026-02-30` — which is
       why this is checked rather than asserted. See the helpers' own header. */
    const at = parse(today);
    if (offset === null || at === null || offset < 0 || offset >= totalDays)
        return null;
    if (unit === 'day') {
        /* `getDay()` is 0 for Sunday; the week starts on Monday, so Sunday is six days in. */
        const weekday = (at.getDay() + 6) % 7;
        const start = offset - weekday;
        return clampSpan(start, 7, totalDays);
    }
    if (unit === 'month') {
        const start = offset - (at.getDate() - 1);
        return clampSpan(start, daysInMonth(at.getFullYear(), at.getMonth()), totalDays);
    }
    /* quarter — back up to the first of its first month, then add that quarter's three months. */
    const firstMonth = Math.floor(at.getMonth() / 3) * 3;
    let back = at.getDate() - 1;
    for (let month = firstMonth; month < at.getMonth(); month += 1) {
        back += daysInMonth(at.getFullYear(), month);
    }
    let length = 0;
    for (let month = firstMonth; month < firstMonth + 3; month += 1) {
        length += daysInMonth(at.getFullYear(), month);
    }
    return clampSpan(offset - back, length, totalDays);
}
/** ⚠️ Clipped to the range at BOTH ends, so a period straddling an edge still draws its visible half. */
function clampSpan(start, length, totalDays) {
    const at = Math.max(0, start);
    const end = Math.min(totalDays, start + length);
    return { at, days: Math.max(1, end - at) };
}
/** ⚠️ Day 0 of the NEXT month is the last day of this one — the leap-year rule for free. */
function daysInMonth(year, month) {
    return new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
}
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ THE DATE HEADER — A BAND NAMING THE PERIOD, A ROW OF UNIT COLUMNS.   │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Item 14, 15 Sep. This was a day ruler that thinned out as days got narrower — at 26px every day
 * numbered, at 5.5px only Mondays, at 2.2px only the first of a month — which meant zooming out
 * removed information and added none, and the numbers that survived were still day-of-month numbers
 * floating over nothing.
 *
 * ⚠️ **It draws whatever `buildScale` produced**, which is the point: the columns are the chart's
 * own geometry rather than a caption over it, so a boundary in the header is a gridline in the
 * chart and the left edge of a bar that starts there.
 */
function ScaleHeader({ scale, headerHeight, rangeFrom, columnStrip, columnTint, ruleEveryDay, }) {
    /* A day column that is not a week's start is ruled only when asked — `ruleEveryDay`. */
    const ruled = (cell) => cell.divider || (ruleEveryDay && cell.days === 1);
    const band = headerHeight / 2;
    const edge = (cell) => {
        const left = scale.x[cell.at] ?? 0;
        const right = scale.x[Math.min(scale.x.length - 1, cell.at + cell.days)] ?? scale.width;
        return { left, width: Math.max(0, right - left) };
    };
    const highlight = scale.highlight === null
        ? null
        : {
            left: scale.x[scale.highlight.at] ?? 0,
            width: (scale.x[Math.min(scale.x.length - 1, scale.highlight.at + scale.highlight.days)] ??
                scale.width) - (scale.x[scale.highlight.at] ?? 0),
        };
    return ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [columnTint === undefined
                ? null
                : scale.columns.map((cell) => {
                    const colour = columnTint({
                        at: cell.at,
                        days: cell.days,
                        from: shift(rangeFrom, cell.at) ?? rangeFrom,
                        to: shift(rangeFrom, cell.at + cell.days - 1) ?? rangeFrom,
                    });
                    if (colour === undefined)
                        return null;
                    return ((0, jsx_runtime_1.jsx)("div", { "aria-hidden": true, className: "pointer-events-none absolute top-0 bottom-0", style: { ...edge(cell), background: colour } }, `tint-${String(cell.at)}`));
                }), highlight === null ? null : ((0, jsx_runtime_1.jsx)("div", { "aria-hidden": true, "data-gantt-period": true, className: "bg-muted/50 pointer-events-none absolute top-0 bottom-0", style: { left: highlight.left, width: highlight.width } })), (0, jsx_runtime_1.jsxs)("div", { className: "bg-[color-mix(in_oklch,var(--muted)_40%,var(--background))] sticky top-0 z-20 border-b", style: { height: headerHeight }, children: [highlight === null ? null : (
                    /* ⚠️ Repeated inside the header because the header has its own background, which would
                       otherwise hide the band exactly where the dates are. */
                    (0, jsx_runtime_1.jsx)("div", { "aria-hidden": true, className: "bg-muted absolute top-0 bottom-0", style: { left: highlight.left, width: highlight.width } })), scale.bands.map((cell) => ((0, jsx_runtime_1.jsx)("span", { className: "text-foreground absolute top-0 flex items-center overflow-hidden border-l px-2 text-[11px] font-medium whitespace-nowrap", style: { ...edge(cell), height: band }, children: cell.label }, `band-${String(cell.at)}`))), scale.columns.map((cell) => ((0, jsx_runtime_1.jsx)("span", { "data-gantt-column": cell.at, 
                        /* ⚠️ Centred, because a column is now a PERIOD and a label pinned to its left edge
                           reads as belonging to the boundary rather than to the span. At the day unit the
                           columns are narrow enough that this is the same thing. */
                        className: (0, cn_1.cn)('text-muted-foreground absolute flex items-center justify-center overflow-hidden px-1 text-[10.5px] whitespace-nowrap', 
                        /* ⚠️ Ruled only where `divider` says — at the day unit that is the week, not the
                           day. `Column.divider` argues it. */
                        ruled(cell) && (cell.strong ? 'border-l border-border' : 'border-l border-border/40')), style: { ...edge(cell), top: band, height: band }, children: columnStrip === undefined ? (cell.label) : (
                        /* ⚠️ The caller's line ABOVE the date, because it is the figure being scanned and
                           the date is what identifies it. Stacked inside the column rather than in a third
                           header band: the band would be one more row of chrome on every chart that does
                           not use this. */
                        (0, jsx_runtime_1.jsxs)("span", { className: "flex flex-col items-center leading-tight", children: [(0, jsx_runtime_1.jsx)("span", { children: columnStrip({
                                        at: cell.at,
                                        days: cell.days,
                                        from: shift(rangeFrom, cell.at) ?? rangeFrom,
                                        to: shift(rangeFrom, cell.at + cell.days - 1) ?? rangeFrom,
                                    }) }), (0, jsx_runtime_1.jsx)("span", { children: cell.label })] })) }, `col-${String(cell.at)}`)))] }), scale.columns
                .filter((cell) => ruled(cell))
                .map((cell) => ((0, jsx_runtime_1.jsx)("div", { "aria-hidden": true, className: (0, cn_1.cn)('pointer-events-none absolute bottom-0 w-px', cell.strong ? 'bg-border' : 'bg-border/40'), style: { left: scale.x[cell.at] ?? 0, top: headerHeight } }, `grid-${String(cell.at)}`)))] }));
}
function Bar({ row, rows, index, rangeFrom, scale, rowHeight, headerHeight, drag, readOnly, labels, onOpen, onDragStart, }) {
    /* While dragging, the bar renders at the previewed dates rather than the saved ones — otherwise
     * it sits still until the request comes back, which reads as the drag not having worked. */
    const shownDates = drag === null ? { start: row.start, end: row.end } : shifted(drag);
    const from = dayCount(rangeFrom, shownDates.start);
    const length = dayCount(shownDates.start, shownDates.end);
    /* ⚠️ **Not drawn at all when a date does not parse** — see the note on `parse`. The count in
     * the footer is what tells somebody a row is missing. */
    if (from === null || length === null)
        return null;
    /* ⚠️ **Inclusive of the end day**, so a task from Monday to Monday is one day wide rather than
     * zero. Off-by-one here is the difference between a visible bar and an invisible one. */
    const span = Math.max(1, length + 1);
    /**
     * ┌───────────────────────────────────────────────────────────────────────┐
     * │ ⚠️⚠️ `+ rowHeight`, AND ITS ABSENCE PUT EVERY BAR ONE ROW TOO HIGH.    │
     * └───────────────────────────────────────────────────────────────────────┘
     *
     * Item 31: *"a single row must not render on the header"*. The chart column opens with `Scale`,
     * which is `headerHeight` tall — exactly like the frozen columns' header beside it — and
     * the bars are absolutely positioned in the same box. So row 0 sat at `y = 0`, which is ON the
     * date strip, while its NAME sat one row below it.
     *
     * ⚠️ **It was invisible with many rows and obvious with one.** Every bar was off by the same
     * amount, so a full chart merely looked misaligned against its own names; a single-row timeline
     * drew its one bar across the months. That is why the feedback names the single-row case.
     */
    const top = index * rowHeight + headerHeight;
    return ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(row.dependencies ?? []).map((dependency) => {
                const predecessor = rows.find((candidate) => candidate.key === dependency.key);
                if (predecessor === undefined)
                    return null;
                const fromX = at(scale, daysOr(rangeFrom, predecessor.end, 0) + 1);
                const toX = at(scale, from);
                const predecessorIndex = rows.indexOf(predecessor);
                return ((0, jsx_runtime_1.jsx)("div", { 
                    /* ⚠️ Violated arrows are the destructive colour; satisfied ones are a hairline. The
                     * distinction is also stated in words under the chart — colour is never the only
                     * carrier. */
                    className: (0, cn_1.cn)('absolute border-t-2 border-dotted', dependency.violated === true ? 'border-destructive' : 'border-border'), style: {
                        left: Math.min(fromX, toX),
                        width: Math.max(2, Math.abs(toX - fromX)),
                        /* ⚠️ Same offset as `top` — an arrow that skipped it would leave the bars and point
                           at where they used to be drawn. */
                        top: predecessorIndex * rowHeight + headerHeight + rowHeight / 2,
                    }, "aria-hidden": true }, dependency.key));
            }), (0, jsx_runtime_1.jsx)("div", { className: "absolute flex items-center", 
                /* ⚠️ The width is the DIFFERENCE between two lookups, never a day count times a width: at
                   the month unit a seven-day bar is wider in February than in March, because February's
                   column holds fewer days in the same pixels. */
                style: {
                    left: at(scale, from),
                    width: Math.max(2, at(scale, from + span) - at(scale, from)),
                    top,
                    height: rowHeight,
                }, children: (0, jsx_runtime_1.jsxs)("div", { className: (0, cn_1.cn)('group relative flex h-[18px] w-full items-center overflow-hidden rounded-[5px] px-1.5', 
                    /* ⚠️ An outline for an undecided band — `GanttRow.barDashed`. */
                    row.barDashed === true && 'border border-dashed bg-transparent'), style: {
                        background: row.barDashed === true
                            ? 'transparent'
                            : (row.barColor ?? row.color ?? 'var(--primary)'),
                        borderColor: row.barDashed === true ? (row.barColor ?? 'var(--primary)') : undefined,
                        /* ⚠⚠️ **The bar's label is MEASURED against the bar, and this was the worst pair in
                         * the product.** A bar is the project's own colour — data, not a token — and the key
                         * written on it (`HSO-5`) sat at **1.73:1 in every one of the eight themes**, found
                         * by sweeping the rendered timeline rather than by reading the source. `readableOn`
                         * picks whichever of the two `--on-id*` foregrounds carries; `--primary-foreground`
                         * is right only for the fallback, where the fill IS the accent. */
                        color: row.barDashed === true
                            ? (row.barColor ?? 'var(--primary)')
                            : ((0, color_1.readableOn)(row.barColor ?? row.color) ?? 'var(--primary-foreground)'),
                    }, children: [(0, jsx_runtime_1.jsx)("button", { type: "button", "aria-label": labels.bar({ ...row, ...shownDates }), 
                            /* ⚠⚠️ **`color: inherit` ON THE BUTTON, AND WITHOUT IT THE FIX ABOVE DOES NOTHING.**
                                 A `<button>` does not inherit `color`: the user-agent stylesheet gives it
                                 `buttontext`, and this project's base layer does not reset that. Setting the
                                 colour on the bar and expecting the label to take it left the key at exactly the
                                 1.73:1 it had before — the browser sweep caught it a second time, unchanged, which
                                 is the only reason it was not called fixed. */
                            style: { color: 'inherit' }, className: (0, cn_1.cn)('min-w-0 flex-1 truncate text-left text-[10px]', readOnly ? '' : 'cursor-grab'), onPointerDown: (event) => {
                                if (readOnly)
                                    return;
                                /* ⚠️ Only the primary button starts a drag — a right-click opening the context menu
                                 * must not leave a drag in progress that never ends. */
                                if (event.button !== 0)
                                    return;
                                onDragStart('move', event.clientX);
                            }, onClick: () => {
                                onOpen?.(row.key);
                            }, children: row.barLabel ?? row.key }), readOnly ? null : ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsx)("span", { role: "presentation", className: "absolute top-0 left-0 h-full w-[6px] cursor-ew-resize", onPointerDown: (event) => {
                                        if (event.button !== 0)
                                            return;
                                        event.stopPropagation();
                                        onDragStart('start', event.clientX);
                                    } }), (0, jsx_runtime_1.jsx)("span", { role: "presentation", className: "absolute top-0 right-0 h-full w-[6px] cursor-ew-resize", onPointerDown: (event) => {
                                        if (event.button !== 0)
                                            return;
                                        event.stopPropagation();
                                        onDragStart('end', event.clientX);
                                    } })] }))] }) })] }));
}
/**
 * A day's left edge in pixels, clamped to the range.
 *
 * ⚠️ **Clamped rather than `undefined`**, because a bar can legitimately start before the range or
 * end after it — a task scheduled outside the window somebody is looking at. Clamping draws it
 * running off the edge, which is true; an unclamped lookup returns `undefined`, `NaN` reaches the
 * style, and the bar silently disappears.
 */
function at(scale, day) {
    const clamped = Math.max(0, Math.min(scale.x.length - 1, day));
    return scale.x[clamped] ?? 0;
}
/**
 * Where a drag has moved the bar to, without committing it.
 *
 * ⚠️ Falls back to the untouched date when the shift cannot be computed, so a bad row stops
 * moving rather than jumping to an arbitrary day.
 */
function shifted(drag) {
    if (drag.edge === 'move') {
        return {
            start: shift(drag.start, drag.days) ?? drag.start,
            end: shift(drag.end, drag.days) ?? drag.end,
        };
    }
    if (drag.edge === 'start') {
        return { start: shift(drag.start, drag.days) ?? drag.start, end: drag.end };
    }
    return { start: drag.start, end: shift(drag.end, drag.days) ?? drag.end };
}
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️ THE SHARED DATE HELPERS RETURN `null`, AND THAT IS NOT NOISE TO SILENCE. │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * `fromWireDate` and `addWireDays` answer `null` for anything that is not `yyyy-mm-dd` **and for a
 * well-formed string naming no real day** — `2026-02-30` parses and then does not round-trip, which
 * `dates.ts` records as *"how a typo in a filter becomes a silent off-by-two"*.
 *
 * Two wrong ways to handle that here, both tempting:
 *
 *  * **`!` or `?? someDate`** — an unparseable date then renders at day 0, so a mis-dated task sits
 *    at the far left of the chart looking like a scheduling decision somebody made;
 *  * **throwing** — inside a render, that takes the whole screen down for one bad row.
 *
 * So an unparseable row is **excluded and counted**, and the footer says how many. Same principle as
 * the dependency conflicts: report it in words rather than hiding it or dying on it.
 */
function parse(wire) {
    return (0, shared_1.fromWireDate)(wire);
}
/** `null` when either end is unparseable — the caller decides what that means. */
function shift(wire, days) {
    return (0, shared_1.addWireDays)(wire, days);
}
/**
 * Whole days from `from` to `to`, or `null` if either end is not a real date.
 *
 * ⚠️ **Computed from UTC noon, not from midnight.** A day is 86 400 000 ms except when it is not:
 * across a daylight-saving boundary it is 23 or 25 hours, and dividing by a fixed day length then
 * yields 0.958 — which `Math.round` mercifully fixes and `Math.floor` does not. Anchoring both ends
 * at noon puts an hour of slack either side of every boundary, so the division is exact.
 */
function dayCount(from, to) {
    const a = parse(from);
    const b = parse(to);
    if (a === null || b === null)
        return null;
    const at = Date.parse(`${(0, shared_1.toWireDate)(a)}T12:00:00Z`);
    const bt = Date.parse(`${(0, shared_1.toWireDate)(b)}T12:00:00Z`);
    return Math.round((bt - at) / 86_400_000);
}
/** `dayCount` where a null cannot mean anything but "do not draw this" — see the header. */
function daysOr(from, to, fallback) {
    return dayCount(from, to) ?? fallback;
}
//# sourceMappingURL=gantt.js.map